import {
  type VisitNoteCategoryDefinition,
  visitNoteCategoryByToken,
} from '@seen/core/models/visitNoteCategory'
import { compact } from 'lodash-es'
import type { JSX, ReactNode } from 'react'
import { LabelColor } from '#src/components/LabelColor.tsx'

interface CategoryChangeActionProps {
  added: string[]
  removed: string[]
}

/*
 * Renders the "added label …" / "removed label …" activity copy for a visit-note
 * category change. Categories are surfaced as "labels" in the feed — identical
 * wording to `LabelChangeAction`, so a category reads the same as a note label —
 * but they resolve differently: labels are user-created `Basic` resources fetched
 * via Medplum, whereas categories are a fixed registry (`VISIT_NOTE_CATEGORIES`)
 * resolved synchronously by `system|code` token, hence a separate renderer.
 */
export function CategoryChangeAction({
  added,
  removed,
}: CategoryChangeActionProps): JSX.Element {
  const addedCategories = resolveCategories(added)
  const removedCategories = resolveCategories(removed)

  return (
    <>
      {addedCategories.length > 0 && (
        <>
          added {addedCategories.length === 1 ? 'label' : 'labels'}{' '}
          <CategoryList categories={addedCategories} />
        </>
      )}
      {addedCategories.length > 0 && removedCategories.length > 0 && ' and '}
      {removedCategories.length > 0 && (
        <>
          removed {removedCategories.length === 1 ? 'label' : 'labels'}{' '}
          <CategoryList categories={removedCategories} />
        </>
      )}
    </>
  )
}

// A token can outlive its registry entry (a retired category); drop any that
// no longer resolves rather than rendering a broken chip.
function resolveCategories(tokens: string[]): VisitNoteCategoryDefinition[] {
  return compact(tokens.map((token) => visitNoteCategoryByToken(token)))
}

function CategoryList({
  categories,
}: {
  categories: VisitNoteCategoryDefinition[]
}): ReactNode {
  return (
    <>
      {categories.map((category, i) => (
        <span key={`${category.system}|${category.code}`}>
          {i > 0 && (i === categories.length - 1 ? ' and ' : ', ')}
          <CategoryItem category={category} />
        </span>
      ))}
    </>
  )
}

function CategoryItem({
  category,
}: {
  category: VisitNoteCategoryDefinition
}): ReactNode {
  return (
    <span className="inline-flex items-center gap-0.5">
      <LabelColor color={category.color} />
      <span className="font-medium text-foreground">{category.display}</span>
    </span>
  )
}
